import {
  BellIcon,
  CreditCardIcon,
  EnvelopeIcon,
  HeadsetIcon,
  LockIcon,
  MapPinIcon,
  PencilSimpleIcon,
  PhoneIcon,
  ShieldCheckIcon,
  SignOutIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react';
import { Fragment, useEffect, type ReactNode } from 'react';

import defaultAvatar from '@/assets/images/profile-avatar.webp';
import { UserAvatar } from '@/components/common/user-avatar';
import { useToast } from '@/components/feedback/toast';
import { routes } from '@/config/routes';
import type { User } from '@/features/auth';
import { DEVICE_STATUS, type DeviceStatus } from '@/features/devices';
import { UnauthorizedError } from '@/lib/api/errors';
import { formatPhoneUS } from '@/lib/utils/format';

import { EDIT_PROFILE_COPY, PROFILE_COPY } from '../constants';
import { useProfile } from '../hooks';
import type { ProfilePanel } from '../schemas';

import styles from './profile-menu.module.scss';

export type ProfileMenuProps = {
  user: User;
  /** Opens the notification-preferences drawer. */
  onOpenNotificationSettings: () => void;
  /**
   * Opens the account-wide Warranty Claims drawer (US-118754).
   *
   * The other non-navigation item. It is a drawer rather than a route because the claims panel is
   * account-scoped chrome that has to outlive the navigation it triggers — see the note in
   * `<TopNav>`.
   */
  onOpenWarrantyClaims: () => void;
  /** Opens the Change Password flow (US-117548) — a modal over the page, not a route. */
  onOpenChangePassword: () => void;
  /** Opens Devices showing only `status`, or everything for `null` — the counter strip. */
  onOpenDevices: (status: DeviceStatus | null) => void;
  /** Opens the Contact Us modal. */
  onOpenContactUs: () => void;
  onNavigate: (to: string) => void;
  onSignOut: () => void;
  /** Opens Edit Profile (US-117543) — the pencil at the header card's top-right — on the saved account. */
  onEditProfile: (identity: ProfilePanel['identity']) => void;
};

type MenuItem = {
  key: string;
  label: string;
  icon: ReactNode;
  onSelect: () => void;
};

const COUNTERS = [
  { key: 'devices', label: 'Devices', status: null, field: 'total' },
  { key: 'off', label: 'Off', status: DEVICE_STATUS.OFF, field: 'off' },
  { key: 'running', label: 'Running', status: DEVICE_STATUS.RUNNING, field: 'running' },
] as const satisfies readonly {
  key: string;
  label: string;
  status: DeviceStatus | null;
  field: keyof NonNullable<ProfilePanel['deviceCounts']>;
}[];

const ICON_SIZE = 20;

/**
 * The Profile panel (US-117535, Figma 1790:954280) — the popover the avatar opens over whatever
 * page the Grower is on.
 *
 * Four stacked regions: a read-only identity header (from `<AuthProvider>`'s user, never edited
 * here), a three-up device counter strip, two brand-green shortcut tiles, and the menu list ending
 * in a red Logout.
 *
 * The body comes from `GET /profile/tracking` via `useProfile`. While it loads the counters are
 * skeletons; a value the server could not supply renders "-" and raises one "Failed to load"
 * toast per fetch with no retry (the AC); a failed call renders every value "-". The Edit Profile
 * pencil is the one control the body gates — it waits for the saved account; every counter, tile
 * and row stays usable, because none of them depends on the numbers it shows.
 */
export function ProfileMenu({
  user,
  onOpenNotificationSettings,
  onOpenWarrantyClaims,
  onOpenChangePassword,
  onOpenDevices,
  onOpenContactUs,
  onNavigate,
  onSignOut,
  onEditProfile,
}: ProfileMenuProps) {
  const {
    data,
    error,
    isError,
    isPending,
    isFetching,
    isFetchedAfterMount,
    dataUpdatedAt,
    errorUpdatedAt,
  } = useProfile();
  const toast = useToast();
  // The profile's identity wins once loaded: a fresh login's session user has no email or phone.
  const { firstName, lastName, email, phone } = data?.identity ?? user;
  const displayName = `${firstName} ${lastName}`.trim();
  // A failed call has no data, so it renders every value unavailable.
  const counts = data?.deviceCounts ?? null;

  // One toast per fetch this open made that came back partial or failed. A cached body shown on a
  // reopen without a fetch raises none (`isFetchedAfterMount`), and a 401 raises none either: the
  // refresh / force-logout seam speaks for it. The timestamps change once per settled fetch.
  const loadFailed =
    isFetchedAfterMount &&
    !isFetching &&
    (Boolean(data?.partial) || (isError && !(error instanceof UnauthorizedError)));
  useEffect(() => {
    if (loadFailed) toast.warning({ title: PROFILE_COPY.LOAD_FAILED, key: 'profile-load-failed' });
  }, [loadFailed, dataUpdatedAt, errorUpdatedAt, toast]);

  const items: MenuItem[] = [
    {
      key: 'notification-preferences',
      label: 'Notification Preferences',
      icon: <BellIcon size={ICON_SIZE} />,
      onSelect: onOpenNotificationSettings,
    },
    {
      key: 'reset-password',
      label: 'Reset Password',
      icon: <LockIcon size={ICON_SIZE} />,
      onSelect: onOpenChangePassword,
    },
    {
      key: 'associate-grower',
      label: 'Associate Grower',
      icon: <UsersThreeIcon size={ICON_SIZE} />,
      onSelect: () => onNavigate(routes.associateGrower),
    },
    {
      key: 'manage-address',
      label: 'Manage Address',
      icon: <MapPinIcon size={ICON_SIZE} />,
      onSelect: () => onNavigate(routes.manageAddress),
    },
    {
      key: 'payment-methods',
      label: 'Payment Methods',
      icon: <CreditCardIcon size={ICON_SIZE} />,
      onSelect: () => onNavigate(routes.paymentMethods),
    },
    {
      key: 'warranty-claims',
      label: 'Warranty Claims',
      icon: <ShieldCheckIcon size={ICON_SIZE} />,
      onSelect: onOpenWarrantyClaims,
    },
    {
      key: 'contact-us',
      label: 'Contact Us',
      icon: <HeadsetIcon size={ICON_SIZE} />,
      onSelect: onOpenContactUs,
    },
  ];

  return (
    <div className={styles.menu}>
      <div className={styles.identity}>
        <div className={styles.identityHead}>
          <UserAvatar name={displayName} src={defaultAvatar} size={42} />
          <p className={styles.name}>{displayName}</p>
          {/* Edit Profile must open from the saved account: a missing email would be sent as `null`. */}
          <button
            type="button"
            className={styles.edit}
            aria-label={EDIT_PROFILE_COPY.TITLE}
            disabled={!data}
            onClick={() => data && onEditProfile(data.identity)}
          >
            <PencilSimpleIcon size={ICON_SIZE} aria-hidden />
          </button>
        </div>
        {email || phone ? (
          <div className={styles.contacts}>
            {email ? (
              <p className={styles.contact}>
                <EnvelopeIcon size={16} aria-hidden />
                <span>{email}</span>
              </p>
            ) : null}
            {phone ? (
              <p className={styles.contact}>
                <PhoneIcon size={16} aria-hidden />
                <span>{`+1 ${formatPhoneUS(phone)}`}</span>
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className={styles.stats} role="group" aria-label="Device counts" aria-busy={isPending}>
        {COUNTERS.map((counter, index) => (
          <Fragment key={counter.key}>
            {index > 0 ? <span className={styles.divider} aria-hidden /> : null}
            <button
              type="button"
              className={styles.stat}
              onClick={() => onOpenDevices(counter.status)}
            >
              {isPending ? (
                <span className={styles.statSkeleton} aria-hidden />
              ) : (
                <span className={styles.statValue}>
                  {counts ? (
                    counts[counter.field]
                  ) : (
                    // "-" is read aloud as "dash"; the accessible name says what it means instead.
                    <>
                      <span aria-hidden>{PROFILE_COPY.UNAVAILABLE}</span>
                      <span className={styles.srOnly}>{PROFILE_COPY.UNAVAILABLE_SR}</span>
                    </>
                  )}
                </span>
              )}
              <span className={styles.statLabel}>{counter.label}</span>
            </button>
          </Fragment>
        ))}
      </div>

      <div className={styles.cards}>
        <button type="button" className={styles.card} onClick={() => onNavigate(routes.support)}>
          <HeadsetIcon size={ICON_SIZE} className={styles.cardIcon} aria-hidden />
          {data && data.activeTickets > 0 ? (
            <span className={styles.cardBadge}>
              {PROFILE_COPY.TICKETS_ACTIVE(data.activeTickets)}
            </span>
          ) : null}
          <span className={styles.cardLabel}>Customer Support</span>
        </button>
        <button
          type="button"
          className={styles.card}
          onClick={() => onNavigate(routes.subscriptions)}
        >
          <CreditCardIcon size={ICON_SIZE} className={styles.cardIcon} aria-hidden />
          <span className={styles.cardLabel}>Manage Subscriptions</span>
        </button>
      </div>

      <ul className={styles.list}>
        {items.map((item) => (
          <li key={item.key}>
            <button type="button" className={styles.item} onClick={item.onSelect}>
              <span className={styles.itemIcon} aria-hidden>
                {item.icon}
              </span>
              {item.label}
            </button>
          </li>
        ))}
        <li>
          <button
            type="button"
            className={[styles.item, styles.danger].join(' ')}
            onClick={onSignOut}
          >
            <span className={styles.itemIcon} aria-hidden>
              <SignOutIcon size={ICON_SIZE} />
            </span>
            Logout
          </button>
        </li>
      </ul>
    </div>
  );
}
